import React, { useEffect, useRef } from "react";
import { useContentContext } from "@/components/content-chat/ContentContext";
import { Card } from "@/components/ui/card";
import { ChaptersListSkeleton } from "@/components/content-chat/skeletons";
import { useContentChapters, useContentChaptersStatus } from "@/hooks/useContentChapters";
import { useGenerateDocumentChapters } from "@/hooks/useDocumentChapters";
import { useGenerateChapters } from "@/hooks/useMediaChapters";
import { Clock } from "lucide-react";
import type { ContentType } from "@/types/content";

const DOCUMENT_TYPES = ["pdf", "doc", "ppt", "web", "text"];
const MEDIA_TYPES = ["youtube", "audio", "video", "recording"];

interface ChaptersIntroScreenProps {
  contentId?: string | null;
  isLoading?: boolean;
  onChapterClick?: (chapter: {startPosition: number;endPosition: number;positionType: 'seconds' | 'pages';}) => void;
}

// Format page reference
function formatPageRef(startPage: number, endPage: number): string {
  if (startPage === endPage) {
    return `Page ${startPage}`;
  }
  return `Pages ${startPage}-${endPage}`;
}

// Format timestamp for media
function formatTimestamp(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor(seconds % 3600 / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export const ChaptersIntroScreen: React.FC<ChaptersIntroScreenProps> = ({
  contentId,
  isLoading = false,
  onChapterClick
}) => {
  const { content } = useContentContext();
  const contentType = content?.type as ContentType | undefined;
  const isDocument = content && DOCUMENT_TYPES.includes(content.type);
  const isMedia = content && MEDIA_TYPES.includes(content.type);

  // Track if we've already triggered generation this session
  const hasTriggeredGeneration = useRef(false);

  // Fetch chapters using unified hook
  const {
    data: chapters,
    isLoading: chaptersLoading
  } = useContentChapters(contentId ?? undefined, contentType);

  const { data: chaptersStatus } = useContentChaptersStatus(contentId ?? undefined, contentType);

  // Generation mutations
  const generateDocumentChapters = useGenerateDocumentChapters();
  const generateMediaChapters = useGenerateChapters();

  const isGenerating = chaptersStatus === 'processing';
  const hasChapters = chapters && chapters.length > 0;
  const showLoading = isLoading || chaptersLoading || isGenerating;

  // Check if content is ready for chapter generation
  const isContentReady = content && (
  isDocument && content.text_extraction_status === 'ready' ||
  isMedia && content.transcript_status === 'ready');


  // Auto-trigger chapter generation when content is ready but no chapters exist
  useEffect(() => {
    if (
    contentId &&
    content &&
    isContentReady &&
    !hasChapters &&
    !chaptersLoading &&
    chaptersStatus !== 'processing' &&
    chaptersStatus !== 'ready' &&
    !hasTriggeredGeneration.current &&
    !generateDocumentChapters.isPending &&
    !generateMediaChapters.isPending)
    {
      console.log('[ChaptersIntroScreen] Auto-triggering chapter generation');
      hasTriggeredGeneration.current = true;

      if (isDocument) {
        generateDocumentChapters.mutate({ contentId });
      } else if (isMedia) {
        generateMediaChapters.mutate(contentId);
      }
    }
  }, [
  contentId,
  content,
  isContentReady,
  hasChapters,
  chaptersLoading,
  chaptersStatus,
  isDocument,
  isMedia,
  generateDocumentChapters,
  generateMediaChapters]
  );

  // Reset trigger flag when content changes
  useEffect(() => {
    hasTriggeredGeneration.current = false;
  }, [contentId]);

  const handleChapterClick = (chapter: typeof chapters[number]) => {
    if (onChapterClick) {
      onChapterClick({
        startPosition: chapter.startPosition,
        endPosition: chapter.endPosition,
        positionType: chapter.positionType
      });
    }
  };

  // Determine status message
  const getStatusMessage = () => {
    if (isGenerating || generateDocumentChapters.isPending || generateMediaChapters.isPending) {
      return "Generating chapters...";
    }
    if (chaptersStatus === 'not_needed') {
      return "This content is too short for chapters.";
    }
    if (!isContentReady && content && chaptersStatus !== 'ready' && chaptersStatus !== 'processing') {
      if (isDocument) {
        return "Waiting for document text extraction to complete...";
      }
      if (isMedia) {
        return "Waiting for transcript extraction to complete...";
      }
    }
    return "Ask Shattara AI to generate chapters to see them here.";
  };

  return (
    <div className="h-full overflow-y-auto px-4 sm:px-0">
      <div className="mx-auto mb-10 w-full max-w-3xl">
        <div className="space-y-6 pt-4">
          {/* Show chapters if available */}
          {hasChapters ?
          <div className="flex flex-col">
              {chapters.map((chapter) =>
            <button
              key={chapter.id}
              type="button"
              onClick={() => handleChapterClick(chapter)}
              className="group cursor-pointer rounded-2xl border border-transparent p-3 pb-4 text-left transition-colors hover:bg-primary/5">

                  {/* Reference badge at top */}
                  <div className="mb-2 flex items-start">
                    <span className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-transparent px-2.5 py-0.5 text-xs font-medium text-primary/80 transition-colors hover:text-foreground bg-popover">
                      {chapter.positionType === 'pages' ?
                  <>
                          
                          {formatPageRef(chapter.startPosition, chapter.endPosition)}
                        </> :

                  <>
                          <Clock className="h-3 w-3" />
                          {formatTimestamp(chapter.startPosition)}
                        </>
                  }
                    </span>
                  </div>
                  {/* Title */}
                  <h3 className="mb-2 line-clamp-1 text-lg text-current">
                    {chapter.title}
                  </h3>
                  {/* Summary - 3-4 lines max, 350 chars */}
                  {chapter.summary &&
              <p className="text-sm leading-relaxed text-muted-foreground">
                      {chapter.summary}
                    </p>
              }
                </button>
            )}
            </div> :

          <>
              {/* Intro Message */}
              <div className="py-5 text-center">
                <p className="text-muted-foreground">
                  {getStatusMessage()}
                </p>
              </div>

              {/* My Chapters - Empty State */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-primary/80">My Chapters</h3>
                {showLoading ?
              <ChaptersListSkeleton type={isDocument ? "page" : "timestamp"} /> :

              <Card className="rounded-2xl border border-primary/10 bg-card p-4 text-sm text-muted-foreground">
                    No chapters yet. Ask Shattara AI to generate{" "}
                    <span className="font-semibold text-foreground">chapters</span>{" "}
                    {isDocument ? "with page references" : "from your content"}.
                  </Card>
              }
              </div>
            </>
          }
        </div>
      </div>
    </div>);

};